import { useNavigate } from 'react-router-dom'
import { LockOutlined, Schedule } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Grid2 as Grid, Paper, Typography } from '@mui/material'
import { useReadUserProfile } from '@/api/user-manager'
import { routes } from '@/routes'
import { CreditProductName } from '@/types/customer-context'
import { creditProducts } from './credit-products'
import { useStartApplication } from './start-application'
import {
  CreditProfileKind,
  lockOf,
  productsFor,
  profileName,
  profileOf,
} from './credit-profile'

type ProductsForYouProps = {
  hasApplicationInProgress: boolean
  currentProduct?: string | null
}

export function ProductsForYou({
  hasApplicationInProgress,
  currentProduct,
}: ProductsForYouProps) {
  const navigate = useNavigate()
  const { data: user } = useReadUserProfile()
  const { start, startingProduct, error } = useStartApplication()

  const profile = profileOf(user?.profile?.occupation)
  const products = productsFor(profile).filter((key) => key !== currentProduct)

  if (!products.length) return null

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1.5,
          mt: 1,
        }}
      >
        <Typography variant="h6">
          {hasApplicationInProgress
            ? 'Outros produtos para você'
            : 'Produtos para você'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Perfil {profileName(profile)}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {products.map((key) => (
          <Grid key={key} size={{ xs: 12, md: products.length >= 3 ? 4 : 6 }}>
            <ProductCard
              productKey={key}
              profile={profile}
              currentSemester={user?.profile?.current_semester ?? null}
              graduationDate={user?.profile?.graduation_date ?? null}
              blocked={hasApplicationInProgress}
              starting={startingProduct === key}
              onOpen={() =>
                navigate(routes.credit.product.replace(':product', key))
              }
              onRoute={navigate}
              onSimulate={start}
            />
          </Grid>
        ))}
      </Grid>
      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}
    </>
  )
}

type ProductCardProps = {
  productKey: keyof typeof creditProducts
  profile: CreditProfileKind
  currentSemester: number | null
  graduationDate: string | null
  blocked: boolean
  starting: boolean
  onOpen: () => void
  onRoute: (path: string) => void
  onSimulate: (product: CreditProductName) => void
}

function ProductCard({
  productKey,
  profile,
  currentSemester,
  graduationDate,
  blocked,
  starting,
  onOpen,
  onRoute,
  onSimulate,
}: ProductCardProps) {
  const product = creditProducts[productKey]
  const lock = lockOf({
    product: productKey,
    profile,
    currentSemester,
    graduationDate,
  })
  const waitsForCurrent = blocked && !lock
  const action = product.action

  return (
    <Paper
      aria-disabled={lock ? true : undefined}
      sx={{
        width: '100%',
        height: '100%',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        border: lock ? '1px dashed' : 0,
        borderColor: 'divider',
        bgcolor: lock ? 'action.hover' : 'background.paper',
        boxShadow: lock ? 'none' : undefined,
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 1.5,
          display: 'grid',
          placeItems: 'center',
          bgcolor: lock ? 'action.selected' : 'primary.50',
          color: lock ? 'text.disabled' : 'primary.light',
        }}
      >
        {lock ? <LockOutlined /> : <product.icon />}
      </Box>

      <Typography
        fontWeight={500}
        fontSize={17}
        color={lock ? 'text.disabled' : 'text.primary'}
      >
        {product.name}
      </Typography>
      <Typography
        variant="body2"
        color={lock ? 'text.disabled' : 'text.secondary'}
        flex={1}
      >
        {product.description}
      </Typography>

      {lock && (
        <Box
          sx={{
            display: 'flex',
            gap: 0.75,
            alignItems: 'flex-start',
            bgcolor: 'warning.light',
            color: 'warning.contrastText',
            borderRadius: 0.75,
            p: 1,
          }}
        >
          <Schedule fontSize="small" />
          <Typography variant="caption">{lock.message}</Typography>
        </Box>
      )}

      {waitsForCurrent && (
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
          <Schedule fontSize="small" color="disabled" />
          <Typography variant="caption" color="text.secondary">
            Disponível depois de concluir ou cancelar a solicitação atual
          </Typography>
        </Box>
      )}

      {!lock && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {action.kind === 'route' ? (
            <Button size="small" onClick={() => onRoute(action.path)}>
              Fazer cotação
            </Button>
          ) : (
            <>
              <Button size="small" variant="outlined" onClick={onOpen}>
                Saiba mais
              </Button>
              {action.kind === 'application' && !waitsForCurrent && (
                <LoadingButton
                  size="small"
                  loading={starting}
                  onClick={() => onSimulate(action.product)}
                >
                  Simule
                </LoadingButton>
              )}
            </>
          )}
        </Box>
      )}
    </Paper>
  )
}
